"use client";

import type { FC } from "react";

import DOMPurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";

import { FaqArticleFeedback } from "@/components/FaqArticleFeedback/FaqArticleFeedback";
import { BUILDER_TEXT_CLASSNAME } from "@/libs/builder/consts";

import type { FaqArticleProps } from "./FaqArticle.builder";

import styles from "./FaqArticle.module.scss";

const sanitize = DOMPurify.sanitize;

type SeoFaqStructuredData = {
  "@context": "https://schema.org";
  "@type": "FAQPage";
  mainEntity: {
    "@type": "Question";
    name: string;
    acceptedAnswer: {
      "@type": "Answer";
      text: string;
    };
  }[];
};

export const FaqArticle: FC<FaqArticleProps> = ({
  question,
  questionHeadlineType,
  seoFriendlyAnswer,
  answer,
  enableArticleFeedback,
}) => {
  const { isFeedbackMechanismEnabled } = useConfig();
  const isFeedbackVisible = isFeedbackMechanismEnabled && (enableArticleFeedback ?? true);
  const HeadlineComponent = questionHeadlineType;
  const seoData: SeoFaqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: sanitize(seoFriendlyAnswer || answer),
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(seoData)}</script>
      <HeadlineComponent className={styles.headline}>{question}</HeadlineComponent>
      <div className={BUILDER_TEXT_CLASSNAME}>
        <div dangerouslySetInnerHTML={{ __html: sanitize(answer) }} />
      </div>
      {isFeedbackVisible && <FaqArticleFeedback referenceName={question} />}
    </>
  );
};
